import { SupabaseClient } from '@supabase/supabase-js';
import {
  InterviewEventData,
  InterviewEventName,
  InterviewEvents
} from '@/types/pulse';
import { normalizeForRespondentPanel } from '@/utils/pulse/panels';

/**
 * Returns the id of the respondent matching (panel, panelist, workspace),
 * inserting the row if it doesn't exist yet.
 *
 * A respondent the workspace deleted resolves to null instead: they take the
 * interview as though their id matched nobody, so nothing re-attaches to a
 * person their workspace removed. Null rather than a fresh row because the
 * deleted one still holds that id under the unique index.
 *
 * `panel` should come straight from getPanelistIdMatch. It goes through
 * normalizeForRespondentPanel() regardless, so a campaign's RESPONDENT_B2B/B2C
 * value can never reach the row — see the invariant on those enum members.
 * Storing a Respondent person under a sub-panel value would put them on a
 * second row under the (panel, panelist_id, workspace_id) unique key, splitting
 * their history and letting a delete on one row miss the other.
 */
export const getOrCreateRespondent = async (
  supabase: SupabaseClient,
  panel: number,
  panelistId: string,
  workspaceId: string
): Promise<number | null> => {
  const storedPanel = normalizeForRespondentPanel(panel);

  const { data: existing } = await supabase
    .from('respondents')
    .select('id, deleted')
    .eq('panel', storedPanel)
    .eq('panelist_id', panelistId)
    .eq('workspace_id', workspaceId)
    .limit(1)
    .maybeSingle()
    .throwOnError();
  if (existing) return existing.deleted ? null : existing.id;

  const { data: created } = await supabase
    .from('respondents')
    .insert({
      panel: storedPanel,
      panelist_id: panelistId,
      workspace_id: workspaceId
    })
    .select('id')
    .single()
    .throwOnError();
  return created.id;
};

export interface InterviewEventWithRespondent {
  respondent_id: number;
  events: InterviewEvents;
  respondents: { panelist_id: string } | null;
}

/**
 * Records one funnel event for a respondent on an interview. One row per
 * (interview, respondent) — repeating a stage updates that key's event_dt to the
 * latest time while `created_at` keeps the first event. Reads the existing
 * `events` first and overrides only the firing key, so the other stages survive.
 *
 * The read+upsert is not atomic, so two overlapping calls would clobber each
 * other. Callers must not overlap: recordPreInterviewEvent in
 * components/interview/livekit/preInterviewEvents.ts chains them so only one
 * runs at a time, which is required because adjacent stages fire in the same
 * render tick.
 *
 * `query_params` is written on every event, not just the first. Omitting it
 * would leave the column out of the upsert's column list, so a row created by
 * some event other than `opened` — possible whenever that call failed — would
 * insert with a null snapshot.
 */
export const upsertInterviewEvent = async <E extends InterviewEventName>(
  supabase: SupabaseClient,
  interviewId: number,
  respondentId: number,
  event: E,
  queryParams: Record<string, unknown>,
  data?: InterviewEventData<E>
): Promise<void> => {
  const { data: existing } = await supabase
    .from('interview_events')
    .select('events')
    .eq('interview_id', interviewId)
    .eq('respondent_id', respondentId)
    .maybeSingle()
    .throwOnError();

  const events = {
    ...((existing?.events as InterviewEvents | null) ?? {}),
    [event]: { event_dt: new Date().toISOString(), ...data }
  } as InterviewEvents;

  await supabase
    .from('interview_events')
    .upsert(
      {
        interview_id: interviewId,
        respondent_id: respondentId,
        events,
        query_params: queryParams
      },
      { onConflict: 'interview_id,respondent_id' }
    )
    .throwOnError();
};

/**
 * All interview_events rows for an interview with each respondent's panelist id
 * — the open side of the Analytics funnel. Fetched in pages to stay under the
 * PostgREST row cap.
 */
export const getInterviewEvents = async (
  supabase: SupabaseClient,
  interviewId: number
): Promise<InterviewEventWithRespondent[]> => {
  const pageSize = 1000;
  const rows: InterviewEventWithRespondent[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data } = await supabase
      .from('interview_events')
      // !inner is what makes the deleted filter below drop the row. On a plain
      // embed PostgREST applies the filter to the embedded resource only, so a
      // deleted respondent's row survives with `respondents: null`.
      .select('respondent_id, events, respondents!inner(panelist_id)')
      .eq('interview_id', interviewId)
      .eq('respondents.deleted', false)
      .order('id', { ascending: true })
      .range(from, from + pageSize - 1)
      .throwOnError();
    const page = (data ?? []) as unknown as InterviewEventWithRespondent[];
    rows.push(...page);
    if (page.length < pageSize) return rows;
  }
};
